import { useEffect } from "react";

import { UseQueryOptions, useSuspenseQuery } from "@tanstack/react-query";

import { useGetCtgListFilterData } from "@/components/business/CtgListContainer/hooks/useGetCtgListFilterData";
import { queryClient } from "@api/queryClient";
import { useCtgListFilterState } from "@stores/ctgList";

import { adaptCTGListData } from "./adaptCTGList.data";
import { ctgListApis } from "./ctgList.service";
import { CTG_QUERY_KEYS } from "../queryKey";

import type {
  CtgFilteringData,
  PostCtgListFilterResponse,
} from "@interface/ctgList.interface";

export const useCtgListFilterQuery = (
  options?: UseQueryOptions<
    PostCtgListFilterResponse,
    unknown,
    PostCtgListFilterResponse,
    [string, CtgFilteringData]
  >,
) => {
  const filteringData = useGetCtgListFilterData();

  const [, setCtgListFilter] = useCtgListFilterState();
  const query = useSuspenseQuery({
    queryKey: [CTG_QUERY_KEYS.ctgListFilter, filteringData],
    queryFn: async () => {
      const response = await ctgListApis.post(filteringData);
      return response;
    },
    select: (data) => adaptCTGListData(data),
    staleTime: 0,
    refetchInterval: 2000,
    ...options,
  });

  useEffect(() => {
    setCtgListFilter((prev) => ({
      ...prev,
      maxPage: query.data.totalPage,
      ctgListDataTotal: query.data.totalCount,
    }));
  }, [query.data.totalCount, query.data.totalPage, setCtgListFilter]);

  return query;
};

export const invalidateCtgListFilterQuery = () => {
  return queryClient.invalidateQueries({
    queryKey: [CTG_QUERY_KEYS.ctgListFilter],
  });
};
